import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const referralLink = 'https://refer.micro1.ai/referral/jobs?referralCode=dea1b6f9-9593-49c7-a563-841574622d5a&utm_source=referral&utm_medium=share&utm_campaign=job_referral';

  useEffect(() => {
    setIsVisible(true);
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Update HTML lang attribute when language changes
    document.documentElement.lang = i18n.language;
    
    // Update meta tags for SEO
    const titleMap: Record<string, string> = {
      uk: 'micro1 - Платформа людського інтелекту для епохи ШІ | Віддалена робота з AI',
      ru: 'micro1 - Платформа человеческого интеллекта для эпохи ИИ | Удалённая работа с AI',
      en: 'micro1 - Human Intelligence Platform for the AI Era | Remote AI Work'
    };
    
    const descMap: Record<string, string> = {
      uk: 'Заробляйте віддалено на платформі micro1, навчаючи передові AI-моделі. Приєднуйтесь до глобальної спільноти експертів у розмітці даних, оцінці моделей та створенні контенту для AGI.',
      ru: 'Зарабатывайте удалённо на платформе micro1, обучая передовые AI-модели. Присоединяйтесь к глобальному сообществу экспертов в разметке данных, оценке моделей и создании контента для AGI.',
      en: 'Earn remotely on the micro1 platform by training cutting-edge AI models. Join a global community of experts in data labeling, model evaluation, and content creation for AGI.'
    };
    
    document.title = titleMap[i18n.language] || titleMap.uk;
    
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', descMap[i18n.language] || descMap.uk);
    }
  }, [i18n.language]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setLangMenuOpen(false);
  };

  const languages = [
    { code: 'uk', label: 'UA', flag: '🇺🇦' },
    { code: 'ru', label: 'RU', flag: '🇷🇺' },
    { code: 'en', label: 'EN', flag: '🇺🇸' }
  ];

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  // Get arrays with type safety
  const categories = (t('forWhom.categories', { returnObjects: true }) || []) as Array<{ title: string; desc: string; icon: string }>;
  const steps = (t('opportunities.steps', { returnObjects: true }) || []) as Array<{ number: string; title: string; desc: string }>;
  const faqItems = (t('faq.items', { returnObjects: true }) || []) as Array<{ q: string; a: string }>;

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/74e9c849fe3801fc69594e8a25b3e428/6460bc2138e22c7c76c468b06a00cfa7.png" 
                alt="micro1 - Human Intelligence Platform" 
                className="h-8 w-auto"
              />
            </div>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              <button onClick={() => scrollToSection('about')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">
                {t('nav.about')}
              </button>
              <button onClick={() => scrollToSection('for-whom')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">
                {t('nav.forWhom')}
              </button>
              <button onClick={() => scrollToSection('opportunities')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">
                {t('nav.opportunities')}
              </button>
              <button onClick={() => scrollToSection('faq')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">
                {t('nav.faq')}
              </button>
            </div>

            {/* CTA & Language Switcher */}
            <div className="hidden lg:flex items-center space-x-4">
              <a 
                href={referralLink} 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-6 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-full hover:bg-teal-700 transition-all shadow-md hover:shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                {t('cta.start')}
              </a>

              {/* Language Switcher */}
              <div className="relative">
                <button 
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className="flex items-center space-x-2 px-4 py-2.5 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                  aria-label="Change language"
                >
                  <span className="text-lg">{currentLang.flag}</span>
                  <span className="text-sm font-medium text-gray-800">{currentLang.label}</span>
                  <i className={`ri-arrow-down-s-line text-gray-600 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {langMenuOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${
                          i18n.language === lang.code ? 'bg-teal-50' : ''
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="text-sm font-medium text-gray-800">{lang.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-gray-800 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-6 border-t border-gray-200">
              <div className="flex flex-col space-y-4">
                <button onClick={() => scrollToSection('about')} className="text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer">
                  {t('nav.about')}
                </button>
                <button onClick={() => scrollToSection('for-whom')} className="text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer">
                  {t('nav.forWhom')}
                </button>
                <button onClick={() => scrollToSection('opportunities')} className="text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer">
                  {t('nav.opportunities')}
                </button>
                <button onClick={() => scrollToSection('faq')} className="text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer">
                  {t('nav.faq')}
                </button>
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-colors cursor-pointer ${
                          i18n.language === lang.code ? 'bg-teal-100' : 'bg-gray-100'
                        }`}
                      >
                        <span>{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <a 
                  href={referralLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-teal-600 text-white text-sm font-semibold rounded-full hover:bg-teal-700 transition-all text-center cursor-pointer"
                >
                  {t('cta.start')}
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-teal-50 z-0"></div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
        
        <div className={`max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-teal-100 text-teal-800 text-sm font-semibold rounded-full mb-6 animate-fade-in">
                {t('hero.badge')}
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6 animate-fade-in-up">
                {t('hero.title')}
              </h1>
              <p className="text-xl lg:text-2xl text-gray-600 mb-4 font-light leading-relaxed animate-fade-in-up animation-delay-200">
                {t('hero.subtitle')}
              </p>
              
              {/* English Language Requirement Badge */}
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-50 border-2 border-amber-200 text-amber-900 rounded-lg mb-8 animate-fade-in-up animation-delay-300">
                <i className="ri-global-line text-lg"></i>
                <span className="text-sm font-semibold">{t('hero.englishRequired')}</span>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-400">
                <a 
                  href={referralLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-teal-600 text-white text-base font-semibold rounded-full hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 text-center whitespace-nowrap cursor-pointer"
                >
                  {t('cta.getStarted')} →
                </a>
                <button 
                  onClick={() => scrollToSection('about')}
                  className="px-8 py-4 bg-white text-gray-900 text-base font-semibold rounded-full hover:bg-gray-50 transition-all border-2 border-gray-200 hover:border-gray-300 text-center whitespace-nowrap cursor-pointer"
                >
                  {t('cta.learnMore')}
                </button>
              </div>
              
              {/* Trust indicators */}
              <div className="mt-12 flex items-center space-x-6 animate-fade-in-up animation-delay-600">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">10,000+</p>
                  <p className="text-xs text-gray-600">{t('finalCta.social')}</p>
                </div>
              </div>
            </div>

            <div className="relative animate-fade-in-up animation-delay-400">
              <div className="relative w-full h-[400px] lg:h-[500px]">
                <img 
                  src="https://public.readdy.ai/ai/img_res/6b73348ba8ff92471d3b07befdf8bd31.jpg"
                  alt="AI Platform Visualization"
                  className="w-full h-full object-contain object-center rounded-3xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - NEW */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-4xl lg:text-5xl font-bold text-teal-400">10,000+</div>
              <p className="text-gray-400 text-sm">{i18n.language === 'en' ? 'Active Specialists' : i18n.language === 'ru' ? 'Активных специалистов' : 'Активних спеціалістів'}</p>
            </div>
            <div className="space-y-2">
              <div className="text-4xl lg:text-5xl font-bold text-teal-400">50+</div>
              <p className="text-gray-400 text-sm">{i18n.language === 'en' ? 'Countries' : i18n.language === 'ru' ? 'Стран' : 'Країн'}</p>
            </div>
            <div className="space-y-2">
              <div className="text-4xl lg:text-5xl font-bold text-teal-400">100+</div>
              <p className="text-gray-400 text-sm">{i18n.language === 'en' ? 'AI Projects' : i18n.language === 'ru' ? 'AI-проектов' : 'AI-проєктів'}</p>
            </div>
            <div className="space-y-2">
              <div className="text-4xl lg:text-5xl font-bold text-teal-400">24/7</div>
              <p className="text-gray-400 text-sm">{i18n.language === 'en' ? 'Platform Access' : i18n.language === 'ru' ? 'Доступ к платформе' : 'Доступ до платформи'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-full mb-6">
                {t('about.badge')}
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                {t('about.title')}
              </h2>
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                <p>{t('about.desc1')}</p>
                <p>{t('about.desc2')}</p>
                <p>{t('about.desc3')}</p>
                
                {/* English Requirement Highlight */}
                <div className="flex items-start space-x-3 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-lg mt-6">
                  <i className="ri-notification-badge-line text-2xl text-amber-600 flex-shrink-0 mt-1"></i>
                  <p className="text-gray-900 font-medium">{t('about.englishNote')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 lg:p-12 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500 rounded-full filter blur-3xl opacity-10"></div>
              <div className="relative z-10">
                <div className="mb-8">
                  <i className="ri-double-quotes-l text-5xl text-teal-400"></i>
                </div>
                <p className="text-2xl lg:text-3xl text-white font-light leading-relaxed mb-8">
                  {t('about.quote')}
                </p>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-full bg-teal-500 flex items-center justify-center text-white font-bold text-lg">
                    M
                  </div>
                  <div>
                    <p className="text-white font-semibold">{t('about.quoteName')}</p>
                    <p className="text-gray-400 text-sm">{t('about.quoteRole')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Whom Section */}
      <section id="for-whom" className="py-24 lg:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('forWhom.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('forWhom.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.isArray(categories) && categories.map((category, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-default">
                <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mb-6">
                  <i className={`${category.icon} text-3xl text-teal-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{category.title}</h3>
                <p className="text-gray-600 leading-relaxed">{category.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Opportunities Section */}
      <section id="opportunities" className="py-24 lg:py-32 bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="mb-16">
            <div className="inline-block px-4 py-2 bg-white/10 text-white text-sm font-semibold rounded-full mb-6">
              {t('opportunities.badge')}
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold mb-8">
              {t('opportunities.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.isArray(steps) && steps.map((step, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 text-gray-900 hover:scale-105 transition-transform cursor-default">
                <div className="text-6xl font-bold text-teal-600 mb-6">{step.number}</div>
                <h3 className="text-xl font-bold mb-4">{step.title}</h3>
                <p className="text-gray-700 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <a 
              href={referralLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 bg-teal-600 text-white text-base font-semibold rounded-full hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              {t('cta.startNow')} →
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 lg:py-32 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('faq.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('faq.subtitle')}
            </p>
          </div>

          <div className="space-y-4">
            {Array.isArray(faqItems) && faqItems.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">{item.q}</span>
                  <i className={`ri-${openFaqIndex === index ? 'subtract' : 'add'}-line text-2xl text-teal-600 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaqIndex === index ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">{item.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 lg:py-32 bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-72 h-72 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-72 h-72 bg-white rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            {t('finalCta.title')}
          </h2>
          <p className="text-xl mb-10 opacity-90">
            {t('finalCta.subtitle')}
          </p>
          <a 
            href={referralLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-10 py-5 bg-white text-teal-700 text-lg font-semibold rounded-full hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            {t('finalCta.button')} →
          </a>

          <div className="mt-16 flex flex-wrap justify-center gap-6 items-center opacity-80">
            <div className="flex -space-x-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="w-10 h-10 rounded-full bg-white/20 border-2 border-white flex items-center justify-center text-white text-xs font-bold backdrop-blur-sm">
                  {String.fromCharCode(64 + i)}
                </div>
              ))}
            </div>
            <p className="text-white text-sm">{t('finalCta.social')}</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-3 gap-12 mb-12">
            <div>
              <img 
                src="https://static.readdy.ai/image/74e9c849fe3801fc69594e8a25b3e428/6460bc2138e22c7c76c468b06a00cfa7.png" 
                alt="micro1" 
                className="h-8 w-auto mb-6 brightness-0 invert"
              />
              <p className="text-gray-400 leading-relaxed mb-6">
                {t('footer.description')}
              </p>
              <div className="flex space-x-4">
                <a href="https://twitter.com/micro1_ai" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all hover:scale-110 cursor-pointer" aria-label="Twitter">
                  <i className="ri-twitter-x-line text-lg"></i>
                </a>
                <a href="https://www.linkedin.com/company/micro1/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all hover:scale-110 cursor-pointer" aria-label="LinkedIn">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="https://www.instagram.com/micro1.ai/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all hover:scale-110 cursor-pointer" aria-label="Instagram">
                  <i className="ri-instagram-line text-lg"></i>
                </a>
              </div>
            </div>

            <div className="lg:col-span-2 grid md:grid-cols-3 gap-8">
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-teal-400">{t('footer.contact')}</h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                  3000 El Camino Real<br />
                  Palo Alto, CA 94306<br />
                  <a href="mailto:support@micro1.ai" className="hover:text-teal-400 transition-colors">support@micro1.ai</a>
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-teal-400">{t('footer.links')}</h4>
                <div className="space-y-2">
                  <button onClick={() => scrollToSection('about')} className="block text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">
                    {t('nav.about')}
                  </button>
                  <button onClick={() => scrollToSection('for-whom')} className="block text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">
                    {t('nav.forWhom')}
                  </button>
                  <button onClick={() => scrollToSection('faq')} className="block text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">
                    {t('nav.faq')}
                  </button>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-teal-400">{t('footer.company')}</h4>
                <div className="space-y-2">
                  <a href={referralLink} target="_blank" rel="noopener noreferrer" className="block text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">
                    {t('footer.careers')}
                  </a>
                  <a href={referralLink} target="_blank" rel="noopener noreferrer" className="block text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">
                    {t('footer.research')}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">© 2025 micro1 Inc.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
